import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Bookmark, BookmarkCheck, Download, ExternalLink } from 'lucide-react';
import { api } from '../lib/api';
import { RichTextView } from '../components/RichTextView';
import { Button, Card, ErrorState, Spinner } from '../components/ui';

interface ContentDetail {
  id: string;
  slug: string;
  type: 'ARTICLE' | 'VIDEO' | 'AUDIO' | 'PDF' | 'LINK';
  title: string;
  summary: string | null;
  body: string | null;
  mediaUrl: string | null;
  durationSec: number | null;
  category: { name: string; slug: string } | null;
  saved: boolean;
}

export function ContentDetailPage() {
  const { contentSlug } = useParams<{ slug: string; contentSlug: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const content = useQuery({
    queryKey: ['me', 'content', 'detail', contentSlug],
    queryFn: () => api.get<ContentDetail>(`/me/content/${contentSlug}`),
    enabled: Boolean(contentSlug),
  });

  const toggleSave = useMutation({
    mutationFn: (id: string) => api.post<{ saved: boolean }>(`/me/content/${id}/save`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['me', 'content'] });
      queryClient.invalidateQueries({ queryKey: ['me', 'saved'] });
    },
  });

  if (content.isError) return <ErrorState error={content.error} onRetry={() => content.refetch()} />;
  if (content.isLoading || !content.data) return <Spinner />;

  const item = content.data!;

  return (
    <article className="max-w-lg lg:max-w-3xl mx-auto">
      <header className="bg-surface-raised border-b border-border px-5 pt-4 pb-5 safe-top">
        <div className="flex items-center justify-between gap-3 mb-3">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-1.5 text-sm text-fg-muted hover:text-fg"
          >
            <ArrowLeft className="size-4" />
            Volver
          </button>

          <button
            onClick={() => toggleSave.mutate(item.id)}
            aria-label={item.saved ? 'Quitar de guardados' : 'Guardar'}
            aria-pressed={item.saved}
            className="p-2 rounded-md text-fg-muted hover:bg-surface hover:text-primary"
          >
            {item.saved ? (
              <BookmarkCheck className="size-5 text-primary" />
            ) : (
              <Bookmark className="size-5" />
            )}
          </button>
        </div>

        {item.category && <p className="text-sm text-primary font-medium">{item.category.name}</p>}
        <h1 className="text-2xl font-semibold tracking-tight mt-1 leading-tight">{item.title}</h1>
        {item.summary && <p className="text-fg-muted mt-2 leading-relaxed">{item.summary}</p>}
      </header>

      <div className="px-5 py-6">
        {item.type === 'VIDEO' && item.mediaUrl && (
          <div className="aspect-video rounded-lg overflow-hidden bg-black mb-6">
            <iframe
              src={item.mediaUrl}
              title={item.title}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              // El video lo carga un tercero (Vimeo, YouTube). El sandbox limita
              // lo que ese contenido puede hacer dentro de la app del paciente.
              sandbox="allow-scripts allow-same-origin allow-presentation"
            />
          </div>
        )}

        {item.type === 'AUDIO' && item.mediaUrl && (
          <Card className="p-4 mb-6">
            <audio controls src={item.mediaUrl} className="w-full" preload="none">
              Tu navegador no puede reproducir este audio.
            </audio>
          </Card>
        )}

        {(item.type === 'PDF' || item.type === 'LINK') && item.mediaUrl && (
          <a href={item.mediaUrl} target="_blank" rel="noopener noreferrer" className="block mb-6">
            <Button variant="outline" fullWidth>
              {item.type === 'PDF' ? <Download className="size-4" /> : <ExternalLink className="size-4" />}
              {item.type === 'PDF' ? 'Descargar el PDF' : 'Abrir el enlace'}
            </Button>
          </a>
        )}

        {item.body && <RichTextView source={item.body} className="text-[16px] leading-relaxed" />}
      </div>
    </article>
  );
}
